import { Box, Button, Divider, Grid, Paper, Typography } from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { setUser } from "../../../ReduxStore/UserSlice";
import { showToast, TOKEN_KEYS, autoLogin } from "../../common/Util/Util";
import { openDashboardInNewTab } from "../../../utils/navigationUtils";
import { useNavigate } from "react-router-dom";
import { getUserData } from "../../../API/API";

const OrganizationVerification = ({
  open,
  onClose,
  formData,
  initialData,
  lenderId,
  preventClose,
  onVerificationSuccess
}) => {
  const [loading, setLoading] = React.useState(false);
  const [isVerified, setIsVerified] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Prevent closing the window
  useEffect(() => {
    if (preventClose) {
      const handleBeforeUnload = (e) => {
        e.preventDefault();
        e.returnValue = "";
        // Clear localStorage when user refreshes
        localStorage.clear();
      };

      window.addEventListener("beforeunload", handleBeforeUnload);
      return () =>
        window.removeEventListener("beforeunload", handleBeforeUnload);
    }
  }, [preventClose]);

  // Helper function to make API calls with temporary token
  const makeApiCallWithTempToken = async (apiCall) => {
    const tempToken = localStorage.getItem("tempJwtToken");
    const originalToken = localStorage.getItem(TOKEN_KEYS.JWT);

    try {
      // Store temp token in the standard JWT location
      if (tempToken) {
        localStorage.setItem(TOKEN_KEYS.JWT, tempToken);
      }

      // Make the API call
      const result = await apiCall();

      // Restore original token if it existed
      if (originalToken) {
        localStorage.setItem(TOKEN_KEYS.JWT, originalToken);
      } else {
        localStorage.removeItem(TOKEN_KEYS.JWT);
      }

      return result;
    } catch (error) {
      // Ensure original token is restored even if there's an error
      if (originalToken) {
        localStorage.setItem(TOKEN_KEYS.JWT, originalToken);
      } else {
        localStorage.removeItem(TOKEN_KEYS.JWT);
      }
      throw error;
    }
  };

  // Function to handle successful verification
  const handleVerificationSuccess = async (userData) => {
    setIsVerified(true);
    // Store permanent JWT and clean up temporary tokens
    const tempToken = localStorage.getItem("tempJwtToken");
    // Store permanent JWT token
    if (tempToken) {
      localStorage.setItem(TOKEN_KEYS.JWT, tempToken);
      localStorage.removeItem("tempJwtToken");
    }
    localStorage.removeItem("tempUuid");

    if (userData?.lender?.lenderId) {
      localStorage.setItem("lenderId", userData.lender.lenderId);
    }

    // Prepare complete user data
    const userDataToStore = {
      ...userData,
      jwtToken: tempToken,
      userType: "lender",
      UserType: "lender",
      isLoggedIn: true,
      name: userData.name || userData.Name,
      Name: userData.name || userData.Name,
      lender: userData.lender || {},
    };

    autoLogin(userDataToStore, dispatch);


    // Show success message
    showToast(
      "Account verified successfully! Redirecting to dashboard...",
      "success"
    );

    // Call the success callback if provided
    if (onVerificationSuccess) {
      onVerificationSuccess();
    }

    // Open dashboard in new tab using the utility function
    setTimeout(() => {
      openDashboardInNewTab();
    }, 500);
  };

  // Periodically check verification status
  useEffect(() => {
    if (!isVerified) {
      let checkInterval;

      const checkVerification = async () => {
        try {
          const tempToken = localStorage.getItem("tempJwtToken");
          const uuid = localStorage.getItem("tempUuid");

          if (tempToken && uuid) {
            const response = await makeApiCallWithTempToken(() =>
              getUserData(uuid)
            );
            if (response?.result?.lender?.verified) {
              // Clear the interval immediately when verified
              if (checkInterval) {
                clearInterval(checkInterval);
              }
              handleVerificationSuccess(response.result);
            }
          }
        } catch (error) {
          console.error(
            "🔴 [OrganizationVerification] Error checking verification status:",
            error
          );
        }
      };

      // Initial check
      checkVerification();

      // Set up interval for subsequent checks
      checkInterval = setInterval(checkVerification, 60000); // Check every 1 minute
      return () => {
        if (checkInterval) {
          clearInterval(checkInterval);
        }
      };
    }
  }, [isVerified, dispatch]);

  const handleComplete = async () => {
    setLoading(true);
    try {
      const tempToken = localStorage.getItem("tempJwtToken");
      const uuid = localStorage.getItem("tempUuid");
      if (!tempToken || !uuid) {
        showToast(
          "Session information missing. Please try logging in again.",
          "error"
        );
        return;
      }

      const response = await makeApiCallWithTempToken(() => getUserData(uuid));
      if (response?.result?.lender?.verified) {
        handleVerificationSuccess(response.result);
      } else {
        showToast(
          "Your account is still pending verification. Please wait for the verification email.",
          "info"
        );
      }
    } catch (error) {
      showToast("Error checking verification status", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: "auto", my: 4 }}>
      <Typography variant="h5" gutterBottom color="primary" sx={{ mb: 3 }}>
        Organisation Verification Process
      </Typography>
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 4 }}>
        To verify your organisation after submitting the initial page, you will
        receive unique identification numbers.
      </Typography>

      <Box
        sx={{
          mb: 4,
          p: 3,
          bgcolor: "rgba(0, 74, 173, 0.03)",
          borderRadius: 2,
          border: "1px solid rgba(0, 74, 173, 0.1)",
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <Box>
              <Typography
                variant="subtitle2"
                sx={{ color: "text.secondary", mb: 1 }}
              >
                Lender ID
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: "#004AAD",
                  fontWeight: "bold",
                  p: 1.5,
                  bgcolor: "white",
                  borderRadius: 1,
                  border: "1px solid rgba(0, 74, 173, 0.2)",
                  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
                }}
              >
                {lenderId}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Send an email to verify{" "}
          <span style={{ color: "#004AAD", fontWeight: "bold" }}>
            @contact@dequitec.com
          </span>{" "}
          with your Lender ID as the subject line.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Ensure the email is sent from your official email address for faster
          verification.
        </Typography>
      </Box>

      <Box
        sx={{
          p: 3,
          bgcolor: "#F8F9FA",
          borderRadius: 2,
          border: "1px solid rgba(0, 0, 0, 0.05)",
          mb: 4,
        }}
      >
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          Important Notes:
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          • Keep both your Lender ID and User ID safe for future reference
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          • You will need these IDs for any support requests
        </Typography>
        {/* <Typography variant="body2" color="text.secondary">
          • Verification usually takes 1-2 business days
        </Typography> */}
      </Box>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4 }}>
        <Button
          variant="contained"
          onClick={handleComplete}
          disabled={loading}
          sx={{
            backgroundColor: "#003A91",
            "&:hover": { backgroundColor: "#002570" },
            height: "40px",
            textTransform: "none",
          }}
        >
          {loading ? "Checking..." : "Check Verification Status"}
        </Button>
      </Box>
    </Paper>
  );
};

export default OrganizationVerification;
